/**
 * Feuille de brouillon — données : les modèles des questions (et les lignes
 * déjà jouées pour reprendre une tentative), puis l'enregistrement des lignes.
 *
 *  - élève : RPC `get_scratch_models_for_student` (modèles publiés des questions
 *    jouables, reliées à une SKILLCARD — migration 0367) ;
 *  - démo anonyme : RPC `get_scratch_models_demo` (migration 0506, feuilles des
 *    questions que la démo expose déjà), rien n'est enregistré ;
 *  - aperçu tuteur : lecture directe de `scratch_models` (RLS : auteur / admin),
 *    brouillons validés par le moteur compris, pour tester avant de publier ;
 *  - une ligne validée part en tâche de fond (`record_scratch_line_v2`), avec
 *    nouvelles tentatives : une panne réseau ne bloque jamais l'élève.
 *
 * Deux versions par question (migration 0532) : l'ancien moteur (`legacy`,
 * ≤ v17) et le nouveau (`kernel`, le noyau). On joue la plus récente que ce
 * moteur sait lire ; une tentative déjà commencée garde, pour une question, la
 * version de ses lignes.
 *
 * Lot V3 : une 3e version, `packs`, jugée par le moteur v2 (`@jeraisonne/brouillon-engine`,
 * chargé à la demande). Seul le player d'exercice la joue (`allowPacks`) ; le Live et les
 * apps pas à jour gardent les autres versions.
 */

import { importWithRetry } from '@/lib/chunk-reload';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  StoredScratchModelSchema,
  isEngineCompatible,
  type ScratchModel,
} from '@jeraisonne/reasoner';
import { supabase } from '@/lib/supabase';
import type { SheetLine, StoredLine } from './scratch-session';
import { PACKS_ENGINE_VERSION, loadPacksModule, type PacksModule } from './sheet-engine';
import type * as V2UiModule from '@/features/brouillon-v2';

/** L'interface du prototype pour les feuilles du moteur v2 (lot V4), chargée à la demande. */
export type V2Ui = typeof V2UiModule;
export function loadV2Ui(): Promise<V2Ui> {
  return importWithRetry(() => import('@/features/brouillon-v2'));
}

export type ScratchMode = 'student' | 'preview' | 'demo' | 'off';

/** Version d'une feuille (migration 0532) : ancien moteur ou noyau ; `packs` = moteur v2 (lot V3). */
export type ScratchTrack = 'legacy' | 'kernel' | 'packs';

export interface ScratchModelEntry {
  questionId: string;
  model: ScratchModel;
  maxHora: number;
  /** aperçu tuteur : feuille pas encore publiée */
  draft: boolean;
  /** version jouée, renvoyée à l'enregistrement des lignes */
  track: ScratchTrack;
  engineVersion: number;
  /** niveau de l'exercice, porté par le modèle d'une feuille `packs` */
  level?: string;
  /**
   * le clavier s'ouvre avec toutes ses touches (chiffres, fraction…) : posé par le banc d'essai du clavier
   * (`check-packs-typing.ts --mark`) sur les feuilles dont une ligne du corrigé ne s'écrit qu'avec « Plus »
   */
  moreKeys?: boolean;
}

export interface ScratchData {
  models: Map<string, ScratchModelEntry>;
  /** lignes déjà jouées, par question (reprise d'une tentative) */
  lines: Map<string, StoredLine[]>;
  /** moteur v2, chargé quand une feuille `packs` est jouée */
  packs?: PacksModule | null;
  /** interface du prototype (lot V4) ; absente : la feuille `packs` garde l'ancienne interface */
  v2?: V2Ui | null;
}

interface RawModel {
  question_block_id?: unknown;
  engine_version?: unknown;
  track?: unknown;
  model?: unknown;
  max_hora?: unknown;
  status?: unknown;
  /** aperçu tuteur seulement : rapport de la porte de validation */
  validation?: unknown;
}
interface RawLine {
  question_block_id?: unknown;
  position?: unknown;
  track?: unknown;
  line_text?: unknown;
  atoms?: unknown;
  kind?: unknown;
  hora?: unknown;
  hinted?: unknown;
}

/** Version d'une feuille : celle de la base, sinon déduite du modèle (réponse d'avant 0532). */
const TRACKS: readonly string[] = ['legacy', 'kernel', 'packs'];
const isTrack = (t: unknown): t is ScratchTrack => typeof t === 'string' && TRACKS.includes(t);

function trackOf(raw: unknown, model: ScratchModel): ScratchTrack {
  if (isTrack(raw)) return raw;
  return model.kernel ? 'kernel' : 'legacy';
}

/**
 * Garde, pour chaque question, LA feuille à jouer parmi celles que ce moteur
 * sait lire : la version des lignes déjà écrites s'il y en a, sinon la plus
 * récente. Ignore le reste sans bruit ; les lignes d'une autre version ne se
 * rejouent pas (elles restent en base, créditées dans leur version).
 */
export function parseScratchPayload(
  raw: { models?: unknown; lines?: unknown },
  { allowPacks = false }: { allowPacks?: boolean } = {},
): ScratchData {
  const candidates = new Map<string, ScratchModelEntry[]>();
  (Array.isArray(raw.models) ? (raw.models as RawModel[]) : []).forEach((m) => {
    if (typeof m.question_block_id !== 'string') return;
    // aperçu : un brouillon refusé par le moteur (ou pas encore vérifié) ne se joue pas
    if ('validation' in m && (m.validation as { ok?: unknown } | null)?.ok !== true) return;
    const parsed = StoredScratchModelSchema.safeParse({
      engine_version: m.engine_version,
      model: m.model,
    });
    if (!parsed.success) return;
    const packs = m.track === 'packs';
    if (
      packs
        ? !allowPacks || parsed.data.engine_version !== PACKS_ENGINE_VERSION
        : !isEngineCompatible(parsed.data.engine_version)
    )
      return;
    const level = (m.model as { level?: unknown } | null)?.level;
    const list = candidates.get(m.question_block_id) ?? [];
    list.push({
      questionId: m.question_block_id,
      model: parsed.data.model,
      maxHora: typeof m.max_hora === 'number' ? m.max_hora : 0,
      draft: m.status !== undefined && m.status !== 'published',
      track: trackOf(m.track, parsed.data.model),
      engineVersion: parsed.data.engine_version,
      ...(packs && typeof level === 'string' ? { level } : {}),
      ...(packs && (m.model as { keyboard?: { more?: unknown } } | null)?.keyboard?.more === true
        ? { moreKeys: true }
        : {}),
    });
    candidates.set(m.question_block_id, list);
  });

  const rows = (Array.isArray(raw.lines) ? (raw.lines as RawLine[]) : [])
    .filter(
      (l) =>
        typeof l.question_block_id === 'string' &&
        Array.isArray(l.atoms) &&
        (l.atoms as unknown[]).every((a) => typeof a === 'string') &&
        (l.kind === 'step' || l.kind === 'kept'),
    )
    .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));
  // version jouée par question : celle de sa première ligne (avant 0532 : ancien moteur
  // si la feuille existe, sinon celle de la seule feuille de la question)
  const played = new Map<string, ScratchTrack | null>();
  rows.forEach((l) => {
    const q = l.question_block_id as string;
    if (!played.has(q)) played.set(q, isTrack(l.track) ? l.track : null);
  });

  const models = new Map<string, ScratchModelEntry>();
  candidates.forEach((list, q) => {
    const newest = [...list].sort(
      (a, b) =>
        a.engineVersion - b.engineVersion ||
        Number(a.track === 'kernel') - Number(b.track === 'kernel'),
    );
    const t = played.get(q);
    const chosen =
      (t ? list.find((e) => e.track === t) : undefined) ??
      (played.has(q) && !t ? list.find((e) => e.track === 'legacy') : undefined) ??
      newest.at(-1)!;
    models.set(q, chosen);
  });

  const lines = new Map<string, StoredLine[]>();
  rows.forEach((l) => {
    const q = l.question_block_id as string;
    const entry = models.get(q);
    const track = isTrack(l.track) ? l.track : null;
    if (entry && track && track !== entry.track) return;
    const list = lines.get(q) ?? [];
    list.push({
      atoms: l.atoms as string[],
      ...(typeof l.line_text === 'string' ? { text: l.line_text } : {}),
      kind: l.kind as 'step' | 'kept',
      hora: typeof l.hora === 'number' ? l.hora : 0,
      hinted: l.hinted === true,
    });
    lines.set(q, list);
  });
  return { models, lines };
}

type UntypedRpc = (
  name: string,
  params: Record<string, unknown>,
) => Promise<{ data: unknown; error: { message?: string } | null }>;

// appelé sur le client (`supabase.rpc` détaché perd son `this`), au moment de
// l'appel : importer ce module ne touche pas au client
const rpc: UntypedRpc = (name, params) =>
  (supabase as unknown as { rpc: UntypedRpc }).rpc(name, params);
const db = () => supabase as unknown as SupabaseClient;

async function fetchRaw(
  exerciseId: string,
  attemptId: string | null,
  mode: ScratchMode,
): Promise<{ models?: unknown; lines?: unknown } | null> {
  if (mode === 'preview') {
    const { data, error } = await db()
      .from('scratch_models')
      .select('question_block_id, engine_version, track, model, max_hora, status, validation')
      .eq('exercise_id', exerciseId)
      .in('status', ['draft', 'published']);
    if (error) throw error;
    return { models: data ?? [], lines: [] };
  }
  if (mode === 'demo') {
    const { data, error } = await rpc('get_scratch_models_demo', { p_exercise_id: exerciseId });
    if (error) throw error;
    return (data ?? {}) as { models?: unknown; lines?: unknown };
  }
  const { data, error } = await rpc('get_scratch_models_for_student', {
    p_exercise_id: exerciseId,
    p_attempt_id: attemptId,
  });
  if (error) throw error;
  const payload = (data ?? {}) as { models?: unknown; lines?: unknown; error?: unknown };
  return payload.error ? null : payload;
}

/**
 * Les feuilles d'un exercice. Une feuille `packs` charge le moteur v2 ; s'il ne se
 * charge pas (réseau, ancien navigateur), on rejoue la version de l'ancien moteur.
 */
export async function loadScratchData(
  raw: { models?: unknown; lines?: unknown } | null,
  load: () => Promise<PacksModule> = loadPacksModule,
  loadUi: () => Promise<V2Ui | null> = loadV2Ui,
): Promise<ScratchData> {
  if (!raw) return { models: new Map(), lines: new Map() };
  const data = parseScratchPayload(raw, { allowPacks: true });
  if (![...data.models.values()].some((e) => e.track === 'packs')) return data;
  try {
    const [packs, v2] = await Promise.all([
      load(),
      // l'interface du prototype : si elle ne se charge pas, l'ancienne interface joue la feuille
      loadUi().catch((e) => {
        console.warn('[scratch] interface v2 indisponible', e);
        return null;
      }),
    ]);
    return { ...data, packs, v2 };
  } catch (e) {
    console.warn('[scratch] moteur v2 indisponible : ancien moteur', e);
    return parseScratchPayload(raw);
  }
}

async function fetchScratch(
  exerciseId: string,
  attemptId: string | null,
  mode: ScratchMode,
): Promise<ScratchData> {
  return loadScratchData(await fetchRaw(exerciseId, attemptId, mode));
}

/** Les feuilles de brouillon de l'exercice (aucune : carte « Brouillon » absente). */
export function useScratchData({
  exerciseId,
  attemptId,
  mode,
  enabled = true,
}: {
  exerciseId: string;
  attemptId: string | null;
  mode: ScratchMode;
  /** faux : l'exercice ne peut pas avoir de feuille (aucune SKILLCARD reliée), rien n'est demandé */
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ['scratch-models', exerciseId, mode, mode === 'student' ? attemptId : null],
    enabled:
      enabled &&
      mode !== 'off' &&
      !!exerciseId &&
      (mode === 'preview' || mode === 'demo' || !!attemptId),
    // Pendant le player, pas de rechargement (les feuilles vivent en mémoire) ;
    // au retour sur l'exercice, relecture fraîche (lignes jouées depuis).
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
    queryFn: () => fetchScratch(exerciseId, attemptId, mode),
  });
}

// ── enregistrement des lignes ────────────────────────────────────────────────

export interface ScratchLinePayload {
  p_attempt_id: string;
  p_question_block_id: string;
  p_atoms: string[];
  p_line_text: string;
  p_kind: 'step' | 'kept';
  p_rule_id: string | null;
  p_hora: number;
  p_hinted: boolean;
  /** version jouée (migration 0532) */
  p_track: ScratchTrack;
  /** bonus au geste (migration 0736) : absent = aucune simplification déclarée */
  p_simplified?: number;
}

export function linePayload(
  attemptId: string,
  questionId: string,
  line: SheetLine,
  track: ScratchTrack,
): ScratchLinePayload {
  return {
    p_attempt_id: attemptId,
    p_question_block_id: questionId,
    p_atoms: line.atoms,
    p_line_text: line.text,
    p_kind: line.kind === 'free' ? 'kept' : 'step',
    p_rule_id: line.rule?.id ?? null,
    p_hora: line.hora,
    p_hinted: line.hinted,
    p_track: track,
    ...(line.simplified ? { p_simplified: line.simplified } : {}),
  };
}

/** Effacer la dernière ligne d'une question (migration 0533). */
export interface EraseLinePayload {
  p_attempt_id: string;
  p_question_block_id: string;
  /** texte de la ligne effacée : la base n'efface que si c'est bien la dernière */
  p_line_text: string;
}

export function erasePayload(
  attemptId: string,
  questionId: string,
  line: SheetLine,
): EraseLinePayload {
  return { p_attempt_id: attemptId, p_question_block_id: questionId, p_line_text: line.text };
}

/** erreurs définitives : la tentative est finie, la feuille dépubliée… inutile d'insister */
const FINAL_ERRORS = [
  'scratch_attempt_finished',
  'scratch_attempt_not_found',
  'scratch_model_not_found',
  'scratch_too_many_lines',
  'scratch_invalid_kind',
  'scratch_invalid_track',
  'scratch_track_mismatch',
  'not_authenticated',
];

export function isFinalScratchError(e: unknown): boolean {
  const msg = String((e as { message?: unknown } | null)?.message ?? e ?? '');
  return FINAL_ERRORS.some((code) => msg.includes(code));
}

/** Une ligne que la base refuserait d'office (bornes de `attempt_scratch_lines`). */
export function isRecordableLine(p: ScratchLinePayload): boolean {
  return p.p_atoms.length >= 1 && p.p_atoms.length <= 120 && p.p_line_text.trim().length > 0;
}

export interface LineRecorder {
  /** Met la ligne dans la file ; la promesse se résout quand la file est vide. */
  record(payload: ScratchLinePayload): Promise<void>;
  /** Efface la dernière ligne, APRÈS les envois déjà dans la file (même ordre que l'élève). */
  erase(payload: EraseLinePayload): Promise<void>;
  /** Lignes encore en attente d'envoi. */
  pending(): number;
  /** Relance tout de suite (retour du réseau). */
  wake(): void;
}

/**
 * File d'envoi : les lignes partent dans l'ordre, une à une. Une panne réseau
 * ne bloque jamais l'élève : la ligne est réessayée (0,5 s, 1 s, 2 s… jusqu'à
 * 30 s entre deux essais, 8 essais) et le retour du réseau relance l'envoi
 * aussitôt. Une erreur définitive (tentative finie, feuille dépubliée…) retire
 * la ligne de la file. Jamais d'erreur remontée à l'élève.
 */
export function createLineRecorder(
  send: (payload: ScratchLinePayload) => Promise<void>,
  opts: {
    maxTries?: number;
    baseDelayMs?: number;
    maxDelayMs?: number;
    /** effacer la dernière ligne (défaut : `sendEraseLine`) */
    erase?: (payload: EraseLinePayload) => Promise<void>;
  } = {},
): LineRecorder {
  const eraseSend = opts.erase ?? sendEraseLine;
  const maxTries = opts.maxTries ?? 8;
  const base = opts.baseDelayMs ?? 500;
  const maxDelay = opts.maxDelayMs ?? 30_000;
  const queue: { run: () => Promise<void>; tries: number }[] = [];
  let running: Promise<void> | null = null;
  let wakeUp: (() => void) | null = null;

  const pause = (ms: number) =>
    new Promise<void>((resolve) => {
      const t = setTimeout(done, ms);
      function done() {
        clearTimeout(t);
        wakeUp = null;
        resolve();
      }
      wakeUp = done;
    });

  async function loop(): Promise<void> {
    while (queue.length) {
      const item = queue[0]!;
      try {
        await item.run();
        queue.shift();
      } catch (e) {
        item.tries += 1;
        if (isFinalScratchError(e) || item.tries >= maxTries) {
          queue.shift();
          continue;
        }
        await pause(Math.min(maxDelay, base * 2 ** (item.tries - 1)));
      }
    }
  }

  function drain(): Promise<void> {
    if (!running) {
      running = loop().finally(() => {
        running = null;
        if (queue.length) void drain();
      });
    }
    return running;
  }

  return {
    record(payload) {
      if (isRecordableLine(payload)) queue.push({ run: () => send(payload), tries: 0 });
      return drain();
    },
    erase(payload) {
      queue.push({ run: () => eraseSend(payload), tries: 0 });
      return drain();
    },
    pending: () => queue.length,
    wake() {
      // le réseau revient : chaque ligne repart avec tous ses essais
      queue.forEach((q) => {
        q.tries = 0;
      });
      wakeUp?.();
    },
  };
}

/** RPC absente : base pas encore migrée (0532). */
function isMissingRpc(e: { message?: string } | null): boolean {
  return (
    !!e &&
    /record_scratch_line_v2/.test(e.message ?? '') &&
    /could not find|not exist/i.test(e.message ?? '')
  );
}

/**
 * Effacer la dernière ligne en base. Base pas encore migrée (0533) : rien à faire,
 * la ligne reste en base (elle réapparaîtrait à la reprise de la tentative).
 */
export async function sendEraseLine(payload: EraseLinePayload): Promise<void> {
  const { error } = await rpc('delete_last_scratch_line', { ...payload });
  if (!error) return;
  if (
    /delete_last_scratch_line/.test(error.message ?? '') &&
    /could not find|not exist/i.test(error.message ?? '')
  ) {
    console.warn('[scratch] effacement non enregistré : migration 0533 absente');
    return;
  }
  throw error;
}

export async function sendScratchLine(payload: ScratchLinePayload): Promise<void> {
  const { p_simplified, ...rest } = payload;
  // bonus au geste : la RPC qui le prend (migration 0736). Sans bonus — le cas de presque toutes les
  // lignes — on garde le chemin éprouvé ; base pas encore migrée : on repart sans le bonus.
  if (p_simplified) {
    const v3 = await rpc('record_scratch_line_v3', { ...payload });
    if (!v3.error) return;
    if (!/record_scratch_line_v3/.test(v3.error.message ?? '')) throw v3.error;
    console.warn('[scratch] bonus au geste non enregistré : migration 0736 absente');
  }
  const { error } = await rpc('record_scratch_line_v2', { ...rest });
  if (!error) return;
  if (isMissingRpc(error)) {
    // base d'avant 0532 : une seule feuille par question, la RPC historique suffit
    const { p_track: _track, ...legacy } = rest;
    const retry = await rpc('record_scratch_line', legacy);
    if (retry.error) throw retry.error;
    return;
  }
  throw error;
}
